import 'bootstrap-daterangepicker/daterangepicker.css';
import 'bootstrap/dist/css/bootstrap.css';
import classnames from 'classnames';
import { format, startOfDay } from 'date-fns';
import moment from 'moment';
import Pagination from 'rc-pagination';
import 'rc-pagination/assets/index.css';
import React, { useEffect, useState } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonGroup,
  Col,
  Container,
  Input,
  Nav,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import {
  setActiveCallsTab,
  getCallsByFilter,
  fetchAllCallsData,
  fetchCallsCount,
} from '../actions/Calls';
import { generateCallsReport } from '../actions/Reports';
import CallsList from '../components/Calls/CallsList';
import CompanyAutocomplete from '../components/CompanyAutocomplete';
import DropdownSelect from '../components/ProposalGenerator/DropdownSelect';
import SetLimitEntries from '../components/SetLimitEntries';
import { ENG_LOCALE } from '../lib/constants';
import Calendar from '../Res/Icon/Calendar.png';
import Company from '../Res/Icon/Company.png';
import NumberList from '../Res/Icon/Number-list.png';
import EPIK_CALLS from '../Res/epik-calls.svg';
import RESET_FILTERS from '../Res/Filter_reset_icon.svg';
import './calls.css';
import Spinner from '../../src/components/Spinner/Spinner';
import TooltipIcon from '../../src/components/EpikBoxes/EpikBoxDetails/TooltipIcon/TooltipIcon';
import { toast } from 'react-toastify';

const Calls = () => {
  const companies = useSelector(state => state.AppState.companies);
  const user = useSelector(state => state.AppState.user);
  const calls = useSelector(state => state.AppState.calls);
  const activeCallsTab = useSelector(state => state.UIState.calls.activeCallsTab);
  const token = localStorage.getItem('jwtToken') || null;

  const dispatch = useDispatch();

  const dropdownFilters = [
    { label: 'Called Number', value: 'destination' },
    { label: 'Calling Number', value: 'callerId' },
    { label: 'Serial Number', value: 'serialNumber' },
    { label: 'Duration', value: 'duration' },
    { label: 'Call Type', value: 'callType' },
  ];

  const durationList = [
    { label: 'Between', value: 'between' },
    { label: 'Equal To', value: 'equalto' },
    { label: 'Greater Than', value: 'greaterthan' },
    { label: 'Less Than', value: 'lessthan' },
  ];

  const lineTypeOpt = [
    { label: 'All', value: 'all' },
    { label: 'Alarm', value: 'alarm' },
    { label: 'Dialout', value: 'dialout' },
    { label: 'Elevator', value: 'elevator' },
    { label: 'Fire Alarm', value: 'fireAlarm' },
    { label: 'Fax', value: 'fax' },
    { label: 'Fax Standard', value: 'faxStandard' },
    { label: 'Hunt', value: 'hunt' },
    { label: 'Page', value: 'page' },
    { label: 'PRI', value: 'pri' },
    { label: 'Modem', value: 'modem' },
    { label: 'OOB', value: 'oob' },
    { label: 'Ringdown', value: 'ringDown' },
    { label: 'Secure Entry', value: 'secureEntry' },
    { label: 'Voicemail', value: 'vmail' },
    { label: 'Voice', value: 'voice' },
    { label: 'Unassigned', value: 'unassigned' },
  ];

  const allowedRoles = [
    'superAdmin',
    'supportEngineer',
    'companyGroupAdmin',
    'companyGroupManager',
    'admin',
    'enterpriseAdmin',
  ];

  const nowAllowedRoles = ['admin', 'enterpriseAdmin'];
  const MAX_CALLS_EXPORT_NUMBER = 350000;
  const MAX_CALLS_EXPORT_MESSAGE = `Max export of records is ${MAX_CALLS_EXPORT_NUMBER}`;
  const MAX_EXPORT_LIMIT_EXCEED = `Max amount of calls exceeded for export.`;

  const [state, setState] = useState({
    date: [startOfDay(new Date()), moment().subtract(5, 'years')._d],
    inFilter: '',
    selectStatusIn: dropdownFilters[0],
    sort: {
      name: '',
      order: '',
    },
    renderFilteredCallsList: calls?.selectedTabCalls?.docs,
    selectedDurationList: durationList[0]?.value,
    durationValue: '',
    durationFrom: '',
    durationTo: '',
    selectedLineType: lineTypeOpt[0]?.value,
    selectedFilterCompany: null,
    paginatedPage: 1,
    sortColumns: { createdOn: -1 },
    clearSearch: false,
    exportTooltip: MAX_CALLS_EXPORT_MESSAGE,
    exportLimitExceeded: false,
    inComingCount: calls?.getCallsCount?.inbound || 0,
    outGoingCount: calls?.getCallsCount?.outbound || 0,
  });

  const [isLoading, setIsLoading] = useState({
    callsLoading: true,
    filterLoading: false,
  });

  useEffect(() => {
    const renderFilteredCallsList = calls.selectedTabCalls.docs;
    setState({
      ...state, 
      renderFilteredCallsList,
      inComingCount: calls?.getCallsCount?.inbound,
      outGoingCount: calls?.getCallsCount?.outbound
    });
  }, [calls]);

  const onFetchCallsData = async () => {
    const checkRoles = allowedRoles?.includes(user.role) && !nowAllowedRoles?.includes(user.role);
    const fetchData = dispatch(
      fetchAllCallsData(
        token,
        checkRoles ? null : user.company._id,
        { page: 1, limit: 10, sort: state.sortColumns },
        state.date,
      ),
    );
    const fetchCount = dispatch(
      fetchCallsCount(token, null, state.date, state.selectedFilterCompany, activeCallsTab),
    );

    Promise.all([fetchData, fetchCount]).then(response => {
      setIsLoading({ ...isLoading, callsLoading: false });
    });
  };

  useEffect(() => {
    if (user?.company?._id) onFetchCallsData();
  }, [user]);

  const getFilteredCallsData = async (
    tab = activeCallsTab,
    cid = state.selectedFilterCompany,
    limit = calls.selectedTabCalls.limit,
    numPage = state.paginatedPage,
    callsFilter = null,
    date = state.date,
    sort = state.sortColumns,
    isExport = false,
  ) => {
    setIsLoading({ ...isLoading, filterLoading: true }); // Set isLoading to true initially

    try {
      const { value } = state.selectStatusIn;
      let filter = { name: value, value: state.inFilter };

      if (['destination', 'callerId', 'serialNumber'].includes(value) && state.inFilter) {
        filter = { ...filter, value: state.inFilter };
      }

      if (value === 'callType' && state.selectedLineType) {
        filter = { ...filter, value: state.selectedLineType };
      }

      if (value === 'duration' && state.durationFrom && state.durationTo) {
        filter = {
          ...filter,
          duration: state.selectedDurationList,
          value: `${state.durationFrom},${state.durationTo}`,
        };
      }

      if (value === 'duration' && state?.durationValue) {
        filter = { ...filter, duration: state.selectedDurationList, value: state?.durationValue };
      }

      if (isExport) {
        await dispatch(
          generateCallsReport(
            token,
            cid,
            'csv',
            date[1],
            date[0],
            activeCallsTab,
            callsFilter || filter,
            { sort },
          ),
        );
      } else {
        await Promise.all([
          dispatch(
            getCallsByFilter(
              token,
              callsFilter || filter,
              { page: numPage, limit: limit || 10, sort },
              date || state.date,
              cid,
              tab,
            ),
          ),
          dispatch(fetchCallsCount(token, callsFilter || filter, date || state.date, cid, tab)),
        ]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading({ ...isLoading, filterLoading: false }); // Set isLoading to false after asynchronous operations are complete
    }
  };

  const handleSetLimit = async limit => {
    // Implement handleSetLimit logic
    getFilteredCallsData(activeCallsTab, state.selectedFilterCompany, limit);
  };

  const handleChangePagination = async numPage => {
    // Implement handleChangePagination logic
    setState({ ...state, paginatedPage: numPage });
    getFilteredCallsData(
      activeCallsTab,
      state.selectedFilterCompany,
      calls.selectedTabCalls.limit,
      numPage,
    );
  };

  const onCallsFilterByCompany = async selectedCompany => {
    const cid = selectedCompany?.suggestion?._id || null;

    getFilteredCallsData(activeCallsTab, cid);

    setState({ ...state, selectedFilterCompany: cid });
  };

  const handleSortColumns = filter => {
    // Implement handleSortColumns logic
    const { sort, paginatedPage } = state;
    const updateSort = { ...sort };

    if (updateSort.name === filter) {
      updateSort.order = updateSort.order === 1 ? -1 : 1;
    } else {
      updateSort.name = filter;
      updateSort.order = 1;
    }
    const sortColumns = { [updateSort.name]: updateSort.order };
    setState({ ...state, sort: updateSort, sortColumns });

    getFilteredCallsData(
      activeCallsTab,
      state.selectedFilterCompany,
      calls.selectedTabCalls.limit,
      paginatedPage,
      null,
      state.date,
      sortColumns,
    );
  };

  const changeCalledNumber = async e => {
    // Implement changeCalledNumber logic
    const { value, name } = e.currentTarget;
    const { selectStatusIn, selectedFilterCompany } = state;
    setState({ ...state, [name]: value?.trim() });

    if (value?.length < 4) return;

    const filter = { name: selectStatusIn.value, value };
    getFilteredCallsData(
      activeCallsTab,
      selectedFilterCompany,
      calls.selectedTabCalls.limit,
      1,
      filter,
    );
  };

  const onStatusSortSelect = selectedOption => {
    setState({ ...state, selectStatusIn: selectedOption, inFilter: '' });
  };

  const onDurationChange = async ev => {
    setState({
      ...state,
      selectedDurationList: ev.target.value,
      durationValue: '',
      durationFrom: '',
      durationTo: '',
    });
  };

  const onDurationValueChange = async ev => {
    const { value, name } = ev.currentTarget;
    setState({ ...state, [name]: value });

    const { value: stateValue } = state?.selectStatusIn;

    if (stateValue === 'duration' && name === 'durationValue') {
      const filter = { name: stateValue, duration: state.selectedDurationList, value };

      if (!value?.split(':')[1]) return;

      getFilteredCallsData(
        activeCallsTab,
        state.selectedFilterCompany,
        calls.selectedTabCalls.limit,
        1,
        filter,
      );
    }

    if (stateValue === 'duration' && name === 'durationFrom') {
      const filter = {
        name: stateValue,
        duration: state.selectedDurationList,
        value: `${value},${state.durationTo}`,
      };
      if (!value?.split(':')[1]) return;
      getFilteredCallsData(
        activeCallsTab,
        state.selectedFilterCompany,
        calls.selectedTabCalls.limit,
        1,
        filter,
      );
    }

    if (stateValue === 'duration' && name === 'durationTo') {
      const filter = {
        name: stateValue,
        duration: state.selectedDurationList,
        value: `${state.durationFrom},${value}`,
      };
      if (!value?.split(':')[1]) return;
      getFilteredCallsData(
        activeCallsTab,
        state.selectedFilterCompany,
        calls.selectedTabCalls.limit,
        1,
        filter,
      );
    }
  };

  const onLineTypeChange = async ev => {
    setState({ ...state, selectedLineType: ev.target.value });
    const filter = { name: state?.selectStatusIn?.value, value: ev.target.value };
    getFilteredCallsData(
      activeCallsTab,
      state.selectedFilterCompany,
      calls.selectedTabCalls.limit,
      1,
      filter,
    );
  };

  const handeleClick = e => {
    // Implement handeleClick logic
    const el = document.querySelector('.daterangepicker');
    el.classList.toggle('show-dp');
  };

  const onApply = async (e, piker) => {
    // Implement onApply logic
    const dataEnd = piker.endDate._d;
    const dataStart = piker.startDate._d;

    setState({ ...state, date: [dataEnd, dataStart] });
    getFilteredCallsData(
      activeCallsTab,
      state.selectedFilterCompany,
      calls.selectedTabCalls.limit,
      1,
      null,
      [dataEnd, dataStart],
    );
  };

  const ranges = {
    Today: [moment(), moment()],
    Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
    'Last 7 Days': [moment().subtract(6, 'days'), moment()],
    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
    'This Month': [moment().startOf('month'), moment().endOf('month')],
    'Last Month': [
      moment()
        .subtract(1, 'month')
        .startOf('month'),
      moment()
        .subtract(1, 'month')
        .endOf('month'),
    ],
  };

  const reportsCheck = onlick => {
    const { inbound, outbound } = calls?.getCallsCount;
    const totalCalls =
      activeCallsTab === 'all'
        ? outbound + inbound
        : activeCallsTab === 'incoming'
        ? inbound
        : outbound;

    if (totalCalls === 0) return false;
    if (totalCalls > MAX_CALLS_EXPORT_NUMBER) {
      if (onlick) toast.info(MAX_EXPORT_LIMIT_EXCEED);
      return false;
    }

    return true;
  };

  const exportCallsCSV = () => {
    const isAllowed = reportsCheck(true);
    if (!isAllowed) return false;

    getFilteredCallsData(
      activeCallsTab,
      state.selectedFilterCompany,
      calls.selectedTabCalls.limit,
      state.paginatedPage,
      null,
      state.date,
      state.sortColumns,
      true,
    );
  };

  const onTabChange = tab => {
    dispatch(setActiveCallsTab(tab));
    getFilteredCallsData(tab, state.selectedFilterCompany, calls.selectedTabCalls.limit, 1);
  };

  const onResetFilters = () => {
    dispatch(setActiveCallsTab('all'));
    getFilteredCallsData(
      'all',
      null,
      10,
      1,
      {},
      [startOfDay(new Date()), moment().subtract(5, 'years')._d],
      { createdOn: -1 },
      false,
    );
    setState({
      date: [startOfDay(new Date()), moment().subtract(5, 'years')._d],
      inFilter: '',
      selectStatusIn: dropdownFilters[0],
      sort: {
        name: '',
        order: '',
      },
      selectedDurationList: durationList[0]?.value,
      durationValue: '',
      durationFrom: '',
      durationTo: '',
      selectedLineType: lineTypeOpt[0]?.value,
      selectedFilterCompany: null,
      paginatedPage: 1,
      sortColumns: { createdOn: -1 },
      clearSearch: !state?.clearSearch,
      exportTooltip: MAX_CALLS_EXPORT_MESSAGE
    });
  };

  useEffect(() => {
    let exportLimitExceeded = true;

    if (state.inComingCount && state.outGoingCount) {
      const isAllowed = reportsCheck(false);
      exportLimitExceeded = !isAllowed;
    }

    setState({
      ...state,
      exportLimitExceeded,
    });
  }, [state.inComingCount, state.outGoingCount, activeCallsTab]);

  return (
    <div>
      {isLoading?.callsLoading && <Spinner />}
      <div className="filter-section" style={{ marginBottom: '20px' }}>
        <div
          className="d-flex justify-content-end"
          lg="12"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '0 15px',
            textAlign: 'right',
          }}
        >
          <Col md={1}>
            <TooltipIcon
              tipId={`${'export-calls'}`}
              onClick={exportCallsCSV}
              imgSrc={EPIK_CALLS}
              displayData={state?.exportTooltip}
              placement="top"
              customStyle={{
                position: 'relative',
                height: '40px',
              }}
              customClass={`analytics-icon calls ${state?.exportLimitExceeded ? 'exceeded' : ''}`}
            />
            <TooltipIcon
              tipId={`${'reset-calls-filter'}`}
              onClick={onResetFilters}
              imgSrc={RESET_FILTERS}
              displayData="Reset filters"
              placement="top"
              customStyle={{
                position: 'relative',
                height: '40px',
              }}
              customClass={`analytics-icon calls`}
            />
          </Col>
          {allowedRoles?.includes(user.role) && (
            <Col
              md={5}
              className="select-status calls-page-select"
              style={{ padding: '0', borderRadius: '0', marginRight: '1rem' }}
            >
              <div className="calls-page-select-cont" style={{ width: '10rem' }}>
                <img style={{ height: '22px' }} src={NumberList} alt="..."></img>
              </div>
              <DropdownSelect
                options={dropdownFilters}
                selectedOption={state?.selectStatusIn}
                onSelect={onStatusSortSelect}
                customDropdownWidth="60%"
              />
              {state?.selectStatusIn?.value === 'duration' ? (
                <div className="duration_wrapper">
                  <Input
                    type="select"
                    className="filter-append-dropdown"
                    onChange={onDurationChange}
                    value={state?.selectedDurationList}
                  >
                    {durationList?.map(el => (
                      <option key={el?.value} value={el?.value}>
                        {el?.label}
                      </option>
                    ))}
                  </Input>
                  {state?.selectedDurationList === 'between' ? (
                    <>
                      <Input
                        name={'durationFrom'}
                        placeholder="Min 00:00"
                        type="text"
                        value={state.durationFrom}
                        onChange={onDurationValueChange}
                        className="duration_from"
                      />

                      <Input
                        name={'durationTo'}
                        placeholder="Max 00:00"
                        type="text"
                        value={state.durationTo}
                        onChange={onDurationValueChange}
                        className="duration_to"
                      />
                    </>
                  ) : (
                    <Input
                      name={'durationValue'}
                      placeholder="Enter duration 00:00"
                      value={state?.durationValue}
                      onChange={onDurationValueChange}
                    />
                  )}
                </div>
              ) : state?.selectStatusIn?.value === 'callType' ? (
                <Input
                  type="select"
                  className="filter-append-dropdown"
                  onChange={onLineTypeChange}
                  value={state?.selectedLineType}
                >
                  {lineTypeOpt?.map(el => (
                    <option key={el?.value} value={el?.value}>
                      {el?.label}
                    </option>
                  ))}
                </Input>
              ) : (
                <Input
                  name={'inFilter'}
                  placeholder="Filter number"
                  value={state?.inFilter}
                  onChange={changeCalledNumber}
                />
              )}
            </Col>
          )}

          {allowedRoles?.includes(user.role) &&
          !nowAllowedRoles?.includes(user.role) &&
          companies.docs ? (
            <Col className="calls-page-select" style={{ display: 'flex' }} md="3">
              <div className="calls-page-select-cont">
                <img style={{ height: '22px' }} src={Company} alt="..."></img>
              </div>
              <CompanyAutocomplete
                searchTerm={''}
                placeholder="Filter Company..."
                companies={companies?.docs}
                onChange={onCallsFilterByCompany}
                clearSearch={state.clearSearch}
              />
            </Col>
          ) : null}
          {state?.date[0] && state?.date[1] && (
            <Col md={2} className="float-right">
              <DateRangePicker
                autoUpdateInput
                startDate={state?.date[1]}
                endDate={state?.date[0]}
                onApply={onApply}
                showCustomRangeLabel
                alwaysShowCalendars
                ranges={ranges}
                opens="left"
                onHide={handeleClick}
                onShow={handeleClick}
              >
                <div className="input-group" style={{ alignItems: 'center' }}>
                  <span className="input-group-btn">
                    <Button
                      className="default date-range-toggle"
                      style={{
                        background: '#DDE6FA',
                        border: 'none',
                        color: '#FF2437',
                        width: '4rem',
                      }}
                    >
                      <img style={{ height: '22px' }} src={Calendar} alt="..."></img>
                    </Button>
                  </span>
                  <div className="view-data-input">
                    {format(state?.date[1], 'MM/dd/yyyy')} - {format(state?.date[0], 'MM/dd/yyyy')}
                  </div>
                </div>
              </DateRangePicker>
            </Col>
          )}
        </div>
      </div>
      <Container fluid style={{ margin: '0' }}>
        <div className="calls-page-content viewContainer">
          <Row>
            <Col md={12}>
              <Nav
                tabs
                style={{
                  marginBottom: '1rem',
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ButtonGroup className="tab-group">
                  <Button
                    className={classnames({ active: activeCallsTab === 'all' })}
                    onClick={() => onTabChange('all')}
                  >
                    All Calls (
                    {parseInt(state.inComingCount || 0) +
                      parseInt(state.outGoingCount || 0)}
                    )
                  </Button>
                  <Button
                    className={classnames({ active: activeCallsTab === 'outgoing' })}
                    onClick={() => onTabChange('outgoing')}
                  >
                    Outgoing Calls ({state.outGoingCount || 0})
                  </Button>
                  <Button
                    className={classnames({ active: activeCallsTab === 'incoming' })}
                    onClick={() => onTabChange('incoming')}
                  >
                    Incoming Calls ({state.inComingCount || 0})
                  </Button>
                </ButtonGroup>
              </Nav>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <TabContent activeTab={activeCallsTab}>
                <TabPane tabId={activeCallsTab}>
                  <Row>
                    <Col md={12}>
                      <CallsList
                        calls={state?.renderFilteredCallsList}
                        onSort={handleSortColumns}
                        sortValue={state?.sort}
                        user={user}
                        tab={activeCallsTab}
                        isLoading={isLoading.filterLoading}
                      />
                    </Col>
                  </Row>

                  {calls?.selectedTabCalls?.totalPages > 1 && (
                    <Row className="justify-content-md-center">
                      <SetLimitEntries
                        value={calls.selectedTabCalls.limit}
                        onChange={handleSetLimit}
                      />
                      <Pagination
                        locale={ENG_LOCALE}
                        className="pagination-container"
                        onChange={handleChangePagination}
                        current={calls.selectedTabCalls.page}
                        total={calls.selectedTabCalls.totalDocs}
                        pageSize={calls.selectedTabCalls.limit}
                        showLessItems
                        showTitle={false}
                        showTotal={(total, range) => `${range[0]} - ${range[1]} of ${total} items`}
                      />
                    </Row>
                  )}
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default Calls;
